/**
 * LK-001 — Главная личного кабинета.
 *
 * Карточка потока + прогресс по 8 модулям. Все навигационные ссылки —
 * через AppShell (bottom nav / sidebar), здесь только контент главной.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { AppShell } from '../../components/AppShell';
import { IdeaCardBlock } from '../../components/IdeaCardBlock';
import { IdeaEvolutionTimeline } from '../../components/IdeaEvolutionTimeline';
import { RoadmapCard } from '../../components/RoadmapCard';
import { api } from '../../lib/api';
import { STATUS_BADGE_CLASS, STATUS_BAR_CLASS, STATUS_LABEL } from '../../lib/module-status';
import type { MeResponse, ModuleListResponse, ModuleProgressStatus } from '../../types/cabinet';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function HomePage() {
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<MeResponse> => (await api.get<MeResponse>('/v1/cabinet/me')).data,
  });
  const modulesQuery = useQuery({
    queryKey: ['modules'],
    queryFn: async (): Promise<ModuleListResponse> =>
      (await api.get<ModuleListResponse>('/v1/cabinet/modules')).data,
  });

  if (meQuery.isLoading || modulesQuery.isLoading) {
    return (
      <AppShell title="Личный кабинет">
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600" />
        </div>
      </AppShell>
    );
  }

  if (meQuery.error || modulesQuery.error) {
    return (
      <AppShell title="Личный кабинет">
        <div className="rounded-2xl bg-red-50 px-4 py-12 text-center">
          <p className="mb-2 text-2xl">😕</p>
          <p className="text-sm text-red-700">Не удалось загрузить кабинет.</p>
        </div>
      </AppShell>
    );
  }

  const me = meQuery.data!;
  const modules = modulesQuery.data!.items;
  const doneCount = modules.filter((m) => m.progress_status === 'done').length;
  const totalCount = modules.length;
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  // «Продолжи»-CTA: первый не закрытый модуль, в который юзеру разрешено зайти.
  // Локед-модуль (paywall) тоже отдадим — он направит на /payments.
  const nextModule = (() => {
    // Сортируем по order_index по возрастанию (бэк возвращает уже так, но
    // защитимся), потом ищем первый со статусом !== done.
    const sorted = [...modules].sort((a, b) => a.order_index - b.order_index);
    return sorted.find((m) => m.progress_status !== 'done');
  })();
  const nextModuleLocked = !!nextModule && !me.has_full_access && nextModule.order_index > 1;

  return (
    <AppShell title="Главная" meta={me.telegram_user_id ? `TG ${me.telegram_user_id}` : undefined}>
      <RoadmapCard />
      <IdeaCardBlock variant="full" me={me} />
      <IdeaEvolutionTimeline />

      {me.enrollment?.cohort ? (
        <section className="mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-white p-5 ring-1 ring-brand-100 dark:from-brand-500/10 dark:to-slate-900 dark:ring-brand-500/20">
          <p className="text-[10px] font-medium uppercase tracking-widest text-brand-700">
            Твой поток
          </p>
          <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">
            {me.enrollment.cohort.title}
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Старт: {formatDate(me.enrollment.cohort.starts_at)}
          </p>
          {totalCount > 0 && (
            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="text-slate-700 dark:text-slate-300">
                  Прогресс: {doneCount} / {totalCount}
                </span>
                <span className="font-semibold text-brand-700">{progressPct}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/60 dark:bg-slate-800">
                <div
                  className="h-full bg-brand-600 transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="mb-6 rounded-2xl bg-amber-50 dark:bg-amber-500/10 p-5 ring-1 ring-amber-100">
          <p className="text-[10px] font-medium uppercase tracking-widest text-amber-700 dark:text-amber-300">
            Поток ещё не назначен
          </p>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            Ты в статусе ENROLLED, но автоматическое распределение в когорту не случилось. Скоро
            админ свяжется и подключит тебя.
          </p>
        </section>
      )}

      {nextModule && (
        <section className="mb-6 rounded-2xl bg-gradient-to-br from-brand-50 to-white p-5 ring-1 ring-brand-200 dark:from-brand-500/10 dark:to-slate-900 dark:ring-brand-500/20">
          <p className="text-[10px] font-medium uppercase tracking-widest text-brand-700 dark:text-brand-300">
            {doneCount === 0 ? 'Начни с этого' : 'Продолжи'}
          </p>
          <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">
            Модуль {nextModule.order_index}: {nextModule.title}
          </h2>
          {nextModule.summary && (
            <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{nextModule.summary}</p>
          )}
          {nextModule.homework_outcome && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              <span className="font-medium text-brand-700 dark:text-brand-300">После ДЗ:</span>{' '}
              {nextModule.homework_outcome}
            </p>
          )}
          {!nextModule.summary && nextModule.description && (
            <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 line-clamp-2">
              {nextModule.description}
            </p>
          )}
          <Link
            to={nextModuleLocked ? '/payments' : `/modules/${nextModule.slug}`}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700"
          >
            {nextModuleLocked
              ? 'Открыть все модули →'
              : nextModule.progress_status === 'in_progress'
                ? 'Продолжить →'
                : 'Перейти к модулю →'}
          </Link>
        </section>
      )}

      {!me.has_full_access && (
        <section className="mb-6 rounded-2xl bg-gradient-to-br from-amber-50 to-white p-5 ring-1 ring-amber-200 dark:from-amber-500/10 dark:to-slate-900 dark:ring-amber-500/30">
          <p className="text-[10px] font-medium uppercase tracking-widest text-amber-700 dark:text-amber-300">
            Бесплатный доступ
          </p>
          <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-100">
            Модуль 1 открыт — остальные после оплаты
          </h2>
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            Пройди Модуль 1, сделай домашку — и решай, идти ли в полный поток. Оплата открывает
            доступ ко всем 8 модулям, AI-трекеру и мастермайндам в группе.
          </p>
          <Link
            to="/payments"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700"
          >
            Открыть все модули →
          </Link>
        </section>
      )}

      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 dark:text-slate-500">
          Модули ({totalCount})
        </h2>
        {modules.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
            Программа потока ещё готовится. Загляни попозже.
          </div>
        ) : (
          <ul className="space-y-2">
            {(() => {
              // Сортировка по order_index — гарантия что Модуль N виден
              // только если Модуль N-1 закрыт.
              const sorted = [...modules].sort((a, b) => a.order_index - b.order_index);
              return sorted.map((m, idx) => {
                const status = m.progress_status as ModuleProgressStatus;
                const payLocked = !me.has_full_access && m.order_index > 1;
                const prev = idx > 0 ? sorted[idx - 1] : null;
                const seqLocked = !!prev && prev.progress_status !== 'done';
                const locked = payLocked || seqLocked;
                return (
                  <li key={m.id}>
                    <Link
                      to={
                        payLocked
                          ? '/payments'
                          : seqLocked
                            ? `/modules/${prev!.slug}`
                            : `/modules/${m.slug}`
                      }
                      className={`group flex items-stretch overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-sm ring-1 ring-slate-200 dark:ring-slate-800 transition-all duration-200 ease-out active:scale-[0.99] hover:-translate-y-0.5 hover:shadow-lg hover:ring-brand-300 dark:hover:ring-brand-500/40 hover:bg-slate-50 dark:hover:bg-slate-800/70 ${
                        locked ? 'opacity-75 hover:opacity-100' : ''
                      }`}
                    >
                      <div
                        className={`w-1.5 ${locked ? 'bg-slate-200' : STATUS_BAR_CLASS[status]}`}
                      />
                      <div className="flex-1 px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-medium text-slate-900 dark:text-slate-100 transition-colors group-hover:text-brand-700 dark:group-hover:text-brand-300">
                            <span className="text-slate-400 dark:text-slate-500">
                              {m.order_index}.
                            </span>{' '}
                            {m.title}
                          </h3>
                          {payLocked ? (
                            <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                              🔒 после оплаты
                            </span>
                          ) : seqLocked ? (
                            <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                              🔒 после Модуля {prev!.order_index}
                            </span>
                          ) : (
                            <span
                              className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_BADGE_CLASS[status]}`}
                            >
                              {STATUS_LABEL[status]}
                            </span>
                          )}
                        </div>
                        {(m.summary || m.goal || m.homework_outcome) && (
                          <dl className="mt-2 space-y-1 text-sm">
                            {m.summary && (
                              <div className="flex gap-2">
                                <dt className="shrink-0 w-20 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 pt-0.5">
                                  О чём
                                </dt>
                                <dd className="text-slate-700 dark:text-slate-200">{m.summary}</dd>
                              </div>
                            )}
                            {m.goal && (
                              <div className="flex gap-2">
                                <dt className="shrink-0 w-20 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 pt-0.5">
                                  Цель
                                </dt>
                                <dd className="text-slate-700 dark:text-slate-200">{m.goal}</dd>
                              </div>
                            )}
                            {m.homework_outcome && (
                              <div className="flex gap-2">
                                <dt className="shrink-0 w-20 text-xs uppercase tracking-wide text-brand-700 pt-0.5">
                                  После ДЗ
                                </dt>
                                <dd className="text-slate-800 dark:text-slate-100 font-medium">
                                  {m.homework_outcome}
                                </dd>
                              </div>
                            )}
                          </dl>
                        )}
                        {!(m.summary || m.goal || m.homework_outcome) && m.description && (
                          <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">
                            {m.description}
                          </p>
                        )}
                        {m.has_video && !locked && (
                          <span className="mt-2 inline-block text-xs text-brand-700">
                            ▶ есть видео
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              });
            })()}
          </ul>
        )}
      </section>
    </AppShell>
  );
}
